<template>
  <div class="home-page app-page">
    <div class="app-workbench">
      <div class="app-workbench__toolbar">
        <div class="toolbar-left">
          <h1 class="app-workbench__title">个人中心</h1>
          <span class="toolbar-subtitle">管理和完善你的个人资料</span>
        </div>
        <button type="button" @click="handleLogout" class="logout-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          退出登录
        </button>
      </div>

      <div class="app-workbench__body app-workbench__body--flush">
        <main class="home-main">
          <!-- 加载状态 -->
          <div v-if="loading" class="loading">
            <div class="spinner"></div>
            <p>加载中...</p>
          </div>

          <!-- 错误提示 -->
          <div v-if="error" class="error-message">{{ error }}</div>

          <!-- 主体内容 -->
          <div v-if="user && !loading" class="user-info-card">

            <!-- ===== 用户头像 & 欢迎区 ===== -->
            <div class="user-header">
              <div class="user-header-content">
                <div class="user-avatar-wrapper">
                  <div class="avatar" @click="handleAvatarClick">
                    <img
                      v-if="user?.avatar"
                      :src="user.avatar"
                      alt="用户头像"
                      class="avatar-img"
                      @error="handleAvatarError"
                    >
                    <svg v-else width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM12 6.75C10.75 6.75 9.75 7.75 9.75 9C9.75 10.25 10.75 11.25 12 11.25C13.25 11.25 14.25 10.25 14.25 9C14.25 7.75 13.25 6.75 12 6.75ZM16.25 16.25C15.06 16.25 14.25 15.31 14.25 14C14.25 13.31 15.44 12.75 16.5 12.25V11C16.5 9.62 15.38 8.5 14 8.5H10C8.62 8.5 7.5 9.62 7.5 11V16H10V11H14V12.06C14.69 12.25 15.25 13.06 15.25 14C15.25 14.38 15.22 14.72 15.13 15H10V16.5H15.63C16.07 16.61 16.25 16.21 16.25 16.25Z" fill="#FF7E7E"/>
                    </svg>
                    <span class="avatar-badge">
                      <svg viewBox="0 0 24 24" width="12" height="12" fill="#fff">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                      </svg>
                    </span>
                  </div>
                </div>
                <div class="user-welcome">
                  <h2 class="user-greeting">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right:8px;flex-shrink:0"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                    欢迎回来，{{ user?.username }}
                  </h2>
                  <p class="user-member-info">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    注册时间：{{ formatDate(user.createdTime) }}
                  </p>
                </div>
              </div>
            </div>

            <!-- ===== 个人信息区域 ===== -->
            <div class="info-section">
              <div class="section-header">
                <div class="section-header-left">
                  <span class="section-accent"></span>
                  <h3>个人信息</h3>
                </div>
                <el-button
                  type="primary"
                  size="small"
                  @click="openEditForm"
                  class="edit-btn"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right:4px"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                  编辑信息
                </el-button>
              </div>

              <div class="info-grid">
                <div class="info-item">
                  <span class="info-item-icon">📋</span>
                  <div class="info-item-body">
                    <span class="label">用户ID</span>
                    <span class="value">{{ user.id }}</span>
                  </div>
                </div>
                <div class="info-item">
                  <span class="info-item-icon">👤</span>
                  <div class="info-item-body">
                    <span class="label">用户名</span>
                    <span class="value">{{ user.username }}</span>
                  </div>
                </div>
                <div class="info-item">
                  <span class="info-item-icon">⚤</span>
                  <div class="info-item-body">
                    <span class="label">性别</span>
                    <span class="value">{{ formatGender(user.gender) }}</span>
                  </div>
                </div>
                <div class="info-item">
                  <span class="info-item-icon">🎂</span>
                  <div class="info-item-body">
                    <span class="label">阳历生日</span>
                    <span class="value">{{ user.birthday ? formatDate(user.birthday) : '未设置' }}</span>
                  </div>
                </div>
                <div class="info-item">
                  <span class="info-item-icon">🌙</span>
                  <div class="info-item-body">
                    <span class="label">阴历生日</span>
                    <span class="value">{{ user.lunarBirthday ? formatDate(user.lunarBirthday) : '未设置' }}</span>
                  </div>
                </div>
                <div class="info-item">
                  <span class="info-item-icon">🏷️</span>
                  <div class="info-item-body">
                    <span class="label">昵称</span>
                    <span class="value">{{ user.nickname || '未设置' }}</span>
                  </div>
                </div>
                <div class="info-item">
                  <span class="info-item-icon">📱</span>
                  <div class="info-item-body">
                    <span class="label">手机号</span>
                    <span class="value">{{ user.phone || '未设置' }}</span>
                  </div>
                </div>
                <div class="info-item">
                  <span class="info-item-icon">✉️</span>
                  <div class="info-item-body">
                    <span class="label">邮箱</span>
                    <span class="value">{{ user.email || '未设置' }}</span>
                  </div>
                </div>
                <div class="info-item">
                  <span class="info-item-icon">📅</span>
                  <div class="info-item-body">
                    <span class="label">注册时间</span>
                    <span class="value">{{ formatDate(user.createdTime) }}</span>
                  </div>
                </div>
                <div class="info-item">
                  <span class="info-item-icon">🔑</span>
                  <div class="info-item-body">
                    <span class="label">最后登录</span>
                    <span class="value">{{ formatDate(user.lastLoginTime) || '从未登录' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- ===== 交友资料区域 ===== -->
            <div class="dating-card">
              <div class="dating-banner">
                <div class="dating-banner-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
                </div>
                <div class="dating-banner-title">交友资料</div>
                <p class="dating-banner-desc">展示给匹配对象的介绍信息，完善资料有助于提升匹配成功率</p>
              </div>
              <div class="dating-body">
                <div v-if="profileLoading" class="dating-loading">
                  <div class="spinner"></div>
                  <p>加载交友资料中...</p>
                </div>
                <template v-else-if="!userProfile && !isEditing">
                  <div class="dating-empty">
                    <el-icon class="dating-empty-icon" :size="48"><UserFilled /></el-icon>
                    <h3 class="dating-empty-title">暂未填写交友资料</h3>
                    <p class="dating-empty-desc">完善昵称、签名、城市等信息，让匹配更精准</p>
                    <el-button type="primary" @click="handleAddProfile">立即完善</el-button>
                  </div>
                </template>
                <template v-else-if="userProfile && !isEditing">
                  <div class="dating-section-head">
                    <h2>资料详情</h2>
                    <el-button type="primary" link @click="handleEditProfile" class="dating-edit-link">
                      <el-icon><Edit /></el-icon>
                      编辑资料
                    </el-button>
                  </div>
                  <div class="dating-fields-grid">
                    <!-- 行1: 所在城市 + 籍贯地 + 职业 -->
                    <div class="dating-field-item">
                      <div class="dating-field-icon">📍</div>
                      <div class="dating-field-info">
                        <h3>所在城市</h3>
                        <p>{{ userProfile.province && userProfile.city ? `${userProfile.province} ${userProfile.city}` : '—' }}</p>
                      </div>
                    </div>
                    <div class="dating-field-item">
                      <div class="dating-field-icon">🏠</div>
                      <div class="dating-field-info">
                        <h3>籍贯地</h3>
                        <p>{{ userProfile.hometown || '—' }}</p>
                      </div>
                    </div>
                    <div class="dating-field-item">
                      <div class="dating-field-icon">💼</div>
                      <div class="dating-field-info">
                        <h3>职业</h3>
                        <p>{{ userProfile.occupation || '—' }}</p>
                      </div>
                    </div>
                    <!-- 行2: 昵称 + 个性签名 + 爱好 -->
                    <div class="dating-field-item">
                      <div class="dating-field-icon">✎</div>
                      <div class="dating-field-info">
                        <h3>昵称</h3>
                        <p>{{ userProfile.nickname || '—' }}</p>
                      </div>
                    </div>
                    <div class="dating-field-item">
                      <div class="dating-field-icon">💬</div>
                      <div class="dating-field-info">
                        <h3>个性签名</h3>
                        <p>{{ userProfile.signature || '—' }}</p>
                      </div>
                    </div>
                    <div class="dating-field-item">
                      <div class="dating-field-icon">🎯</div>
                      <div class="dating-field-info">
                        <h3>爱好</h3>
                        <div class="dating-tags">
                          <el-tag
                            v-for="(hobby, idx) in (userProfile.hobbies ? userProfile.hobbies.split(',') : []).filter(Boolean)"
                            :key="idx"
                            type="info"
                            size="small"
                          >
                            {{ hobby }}
                          </el-tag>
                          <span v-if="!userProfile.hobbies" class="dating-dash">—</span>
                        </div>
                      </div>
                    </div>
                    <!-- 行3: 生活照 (独占一行) -->
                    <div v-if="userProfile && getPhotosList(userProfile.lifePhotos).length > 0" class="dating-field-item dating-field-item--wide">
                      <div class="dating-field-icon">📸</div>
                      <div class="dating-field-info">
                        <h3>生活照</h3>
                        <div class="photos-preview">
                          <img v-for="(url, i) in getPhotosList(userProfile.lifePhotos)" :key="i" :src="url" @click="previewPhoto(url)">
                        </div>
                      </div>
                    </div>
                    <!-- 行4: 身高 + 体重 + 更新时间 -->
                    <div class="dating-field-item">
                      <div class="dating-field-icon">📏</div>
                      <div class="dating-field-info">
                        <h3>身高</h3>
                        <p>{{ userProfile.height ? `${userProfile.height} cm` : '—' }}</p>
                      </div>
                    </div>
                    <div class="dating-field-item">
                      <div class="dating-field-icon">⚖</div>
                      <div class="dating-field-info">
                        <h3>体重</h3>
                        <p>{{ userProfile.weight ? `${userProfile.weight} kg` : '—' }}</p>
                      </div>
                    </div>
                    <div class="dating-field-item">
                      <div class="dating-field-icon">🕐</div>
                      <div class="dating-field-info">
                        <h3>更新时间</h3>
                        <p>{{ formatProfileDate(userProfile.updatedTime) || '—' }}</p>
                      </div>
                    </div>
                  </div>
                </template>
                <template v-else-if="isEditing">
                  <div class="dating-form-head">
                    <h2>{{ userProfile ? '编辑交友资料' : '新增交友资料' }}</h2>
                    <el-button type="primary" link @click="handleProfileCancel">取消</el-button>
                  </div>
                  <el-form
                    ref="profileFormRef"
                    :model="profileForm"
                    label-width="120px"
                    class="dating-profile-form"
                  >
                    <el-form-item
                      label="昵称"
                      prop="nickname"
                      :rules="[{ required: true, message: '请输入昵称', trigger: 'blur' }, { max: 50, message: '昵称不超过50个字符', trigger: 'blur' }]"
                    >
                      <el-input v-model="profileForm.nickname" placeholder="请输入昵称" maxlength="50" />
                    </el-form-item>
                    <el-form-item
                      label="个性签名"
                      prop="signature"
                      :rules="[{ max: 255, message: '个性签名不超过255个字符', trigger: 'blur' }]"
                    >
                      <el-input v-model="profileForm.signature" type="textarea" :rows="2" placeholder="请输入个性签名" maxlength="255" />
                    </el-form-item>
                    <el-form-item
                      label="所在城市"
                      prop="city"
                      :rules="[{ required: true, message: '请选择所在城市', trigger: 'change' }]"
                    >
                      <el-cascader
                        v-model="profileForm.city"
                        :options="cityOptions"
                        :props="{ label: 'name', value: 'name', children: 'children' }"
                        placeholder="请选择省/市"
                        clearable
                        style="width: 100%; max-width: 400px;"
                      />
                    </el-form-item>
                    <el-form-item
                      label="职业"
                      prop="occupation"
                      :rules="[{ required: true, message: '请选择或输入职业', trigger: ['blur', 'change'] }]"
                    >
                      <el-select v-model="profileForm.occupation" placeholder="请选择职业" clearable @change="handleOccupationChange">
                        <el-option v-for="job in commonOccupations" :key="job" :label="job" :value="job" />
                        <el-option label="其他" value="other" />
                      </el-select>
                      <el-input
                        v-if="profileForm.occupation === 'other'"
                        v-model="otherOccupation"
                        placeholder="请输入您的职业"
                        class="dating-other-job"
                        @input="updateOtherOccupation"
                      />
                    </el-form-item>
                    <el-form-item label="爱好" prop="selectedHobbies" :rules="[{ validator: validateHobbyCount, trigger: 'change' }]">
                      <div class="hobby-select-container">
                        <div class="hobby-category">
                          <span class="category-label">运动类</span>
                          <el-checkbox-group v-model="profileForm.selectedHobbies" class="hobby-checkbox-group" @change="handleHobbyChange">
                            <el-checkbox v-for="h in hobbySports" :key="h" :label="h" size="small" />
                          </el-checkbox-group>
                        </div>
                        <div class="hobby-category">
                          <span class="category-label">文艺类</span>
                          <el-checkbox-group v-model="profileForm.selectedHobbies" class="hobby-checkbox-group" @change="handleHobbyChange">
                            <el-checkbox v-for="h in hobbyArts" :key="h" :label="h" size="small" />
                          </el-checkbox-group>
                        </div>
                        <div class="hobby-category">
                          <span class="category-label">生活类</span>
                          <el-checkbox-group v-model="profileForm.selectedHobbies" class="hobby-checkbox-group" @change="handleHobbyChange">
                            <el-checkbox v-for="h in hobbyLife" :key="h" :label="h" size="small" />
                          </el-checkbox-group>
                        </div>
                        <div class="hobby-category">
                          <span class="category-label">兴趣类</span>
                          <el-checkbox-group v-model="profileForm.selectedHobbies" class="hobby-checkbox-group" @change="handleHobbyChange">
                            <el-checkbox v-for="h in hobbyFun" :key="h" :label="h" size="small" />
                          </el-checkbox-group>
                        </div>
                      </div>
                      <div class="hobby-tip">最多选择10个爱好，已选 {{ profileForm.selectedHobbies.length }} 个</div>
                    </el-form-item>
                    <el-form-item
                      label="身高(cm)"
                      prop="height"
                      :rules="[{ type: 'number', min: 100, max: 250, message: '身高范围100-250cm', trigger: 'blur' }]"
                    >
                      <el-input v-model.number="profileForm.height" type="number" min="100" max="250" placeholder="身高" style="max-width: 200px;" />
                    </el-form-item>
                    <el-form-item
                      label="体重(kg)"
                      prop="weight"
                      :rules="[{ type: 'number', min: 30, max: 200, message: '体重范围30-200kg', trigger: 'blur' }]"
                    >
                      <el-input v-model.number="profileForm.weight" type="number" min="30" max="200" placeholder="体重" style="max-width: 200px;" />
                    </el-form-item>
                    <el-form-item label="籍贯地" prop="hometown">
                      <el-input v-model="profileForm.hometown" placeholder="例：广东省广州市" style="max-width: 300px;" />
                    </el-form-item>
                    <el-form-item label="生活照">
                      <div class="photo-upload-area">
                        <div class="photo-grid">
                          <div v-for="(url, i) in profileForm.lifePhotos" :key="i" class="photo-item">
                            <img :src="url" alt="">
                            <button class="photo-del" @click.prevent="removePhoto(i)">×</button>
                          </div>
                          <div v-if="(profileForm.lifePhotos || []).length < 9" class="photo-add" @click="selectPhoto">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#B2BEC3" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                            <span>{{ (profileForm.lifePhotos || []).length }}/9</span>
                          </div>
                        </div>
                        <p class="photo-tip">最多上传9张，支持JPG/PNG格式</p>
                      </div>
                    </el-form-item>
                    <el-form-item class="dating-form-actions">
                      <el-button type="primary" :loading="submitLoading" @click="handleProfileSubmit">
                        {{ userProfile ? '保存修改' : '提交资料' }}
                      </el-button>
                      <el-button @click="handleProfileCancel">取消</el-button>
                    </el-form-item>
                  </el-form>
                </template>
              </div>
            </div>

            <!-- ===== 匹配偏好设置区域 ===== -->
            <div class="preference-section">
              <div class="preference-header">
                <div class="preference-header-left">
                  <span class="section-accent section-accent--pref"></span>
                  <div>
                    <h2>匹配偏好设置</h2>
                    <p>设置您的理想匹配条件，我们将为您精准推荐</p>
                  </div>
                </div>
              </div>

              <div v-if="preferenceLoading" class="loading preference-loading">
                <div class="spinner"></div>
                <p>加载中，请稍候...</p>
              </div>

              <div v-if="preferenceError" class="error-message">{{ preferenceError }}</div>

              <el-form
                ref="preferenceFormRef"
                :model="preferenceForm"
                label-width="120px"
                v-else
                class="preference-form"
              >
                <el-form-item label="最小年龄" prop="minAge">
                  <el-input-number
                    v-model="preferenceForm.minAge"
                    :min="18"
                    :max="100"
                    class="form-control"
                    placeholder="请输入最小年龄"
                  />
                </el-form-item>

                <el-form-item label="最大年龄" prop="maxAge">
                  <el-input-number
                    v-model="preferenceForm.maxAge"
                    :min="preferenceForm.minAge"
                    :max="100"
                    class="form-control"
                    placeholder="请输入最大年龄"
                  />
                </el-form-item>

                <el-form-item label="目标性别" prop="targetGender">
                  <el-select
                    v-model="preferenceForm.targetGender"
                    class="form-control"
                    placeholder="请选择目标性别"
                  >
                    <el-option label="不限" :value="0" />
                    <el-option label="男" :value="1" />
                    <el-option label="女" :value="2" />
                  </el-select>
                </el-form-item>

                <el-form-item label="学历要求" prop="education">
                  <el-select
                    v-model="preferenceForm.education"
                    class="form-control"
                    placeholder="请选择学历要求"
                  >
                    <el-option label="不限" :value="0" />
                    <el-option label="专科" :value="1" />
                    <el-option label="本科" :value="2" />
                    <el-option label="硕士" :value="3" />
                    <el-option label="博士" :value="4" />
                  </el-select>
                </el-form-item>

                <el-form-item label="婚姻状况" prop="maritalStatus">
                  <el-select
                    v-model="preferenceForm.maritalStatus"
                    class="form-control"
                    placeholder="请选择婚姻状况"
                  >
                    <el-option label="不限" :value="0" />
                    <el-option label="未婚" :value="1" />
                    <el-option label="离异" :value="2" />
                  </el-select>
                </el-form-item>

                <el-form-item label="身高范围(cm)" prop="heightRange">
                  <div class="range-container">
                    <el-input-number
                      v-model="preferenceForm.minHeight"
                      :min="120"
                      :max="preferenceForm.maxHeight - 1"
                      class="form-control range-input"
                      placeholder="最小"
                    />
                    <span class="range-separator">-</span>
                    <el-input-number
                      v-model="preferenceForm.maxHeight"
                      :min="preferenceForm.minHeight + 1"
                      :max="220"
                      class="form-control range-input"
                      placeholder="最大"
                    />
                  </div>
                </el-form-item>

                <el-form-item label="体重范围(kg)" prop="weightRange">
                  <div class="range-container">
                    <el-input-number
                      v-model="preferenceForm.minWeight"
                      :min="40"
                      :max="preferenceForm.maxWeight ? preferenceForm.maxWeight - 1 : 100"
                      class="form-control range-input"
                      placeholder="最小"
                    />
                    <span class="range-separator">-</span>
                    <el-input-number
                      v-model="preferenceForm.maxWeight"
                      :min="preferenceForm.minWeight ? preferenceForm.minWeight + 1 : 40"
                      :max="100"
                      class="form-control range-input"
                      placeholder="最大"
                    />
                  </div>
                </el-form-item>

                <el-form-item label="最大距离" prop="maxDistance">
                  <el-select
                    v-model="preferenceForm.maxDistance"
                    class="form-control"
                    placeholder="请选择最大距离"
                  >
                    <el-option label="一公里" :value="1000" />
                    <el-option label="两公里" :value="2000" />
                    <el-option label="五公里" :value="5000" />
                    <el-option label="十公里" :value="10000" />
                    <el-option label="五十公里" :value="50000" />
                    <el-option label="一百公里" :value="100000" />
                    <el-option label="五百公里" :value="500000" />
                    <el-option label="不限" :value="1000000000" />
                  </el-select>
                </el-form-item>

                <el-form-item label="偏好标签" prop="preferredTags">
                  <div class="tags-container">
                    <el-checkbox-group
                      v-model="preferenceForm.preferredTags"
                      :max="5"
                      class="tags-checkbox-group"
                    >
                      <el-checkbox
                        v-for="tag in allTags"
                        :key="tag.value"
                        :label="tag.value"
                        class="tag-checkbox"
                      >
                        {{ tag.label }}
                      </el-checkbox>
                    </el-checkbox-group>
                    <p class="tags-hint">最多选择5个标签</p>
                  </div>
                </el-form-item>

                <el-form-item class="form-actions">
                  <el-button type="primary" @click="savePreference" class="save-btn">保存设置</el-button>
                  <el-button type="default" @click="resetPreferenceForm" class="reset-btn">重置</el-button>
                </el-form-item>
              </el-form>
            </div>

          </div>
        </main>
      </div>
    </div>

    <!-- 编辑个人信息对话框 -->
    <el-dialog
      title="编辑个人信息"
      v-model="dialogVisible"
      width="500px"
      :close-on-click-modal="false"
      :append-to-body="true"
    >
      <el-form
        :model="editForm"
        ref="editFormRef"
        :rules="formRules"
        label-width="100px"
        class="edit-form"
      >
        <el-form-item label="昵称" prop="nickname">
          <el-input v-model="editForm.nickname" placeholder="请输入昵称" maxlength="20"></el-input>
        </el-form-item>

        <el-form-item label="性别" prop="gender">
          <el-radio-group v-model="editForm.gender">
            <el-radio :label="0">未知</el-radio>
            <el-radio :label="1">男</el-radio>
            <el-radio :label="2">女</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="阳历生日" prop="birthday">
          <el-date-picker
            v-model="editForm.birthday"
            type="datetime"
            placeholder="选择阳历生日"
            format="YYYY-MM-DD HH:mm:ss"
            value-format="YYYY-MM-DD HH:mm:ss"
            :disabled-date="disableFutureDate"
          ></el-date-picker>
          <div class="form-hint">阳历生日用于星座匹配，请尽量填写真实信息</div>
        </el-form-item>

        <el-form-item label="阴历生日" prop="lunarBirthday">
          <el-date-picker
            v-model="editForm.lunarBirthday"
            type="datetime"
            placeholder="选择阴历生日"
            format="YYYY-MM-DD HH:mm:ss"
            value-format="YYYY-MM-DD HH:mm:ss"
            :disabled-date="disableFutureDate"
          ></el-date-picker>
          <div class="form-hint">阴历生日用于AI八字匹配计算，请尽量填写真实信息</div>
        </el-form-item>

        <el-form-item label="手机号" prop="phone">
          <el-input v-model="editForm.phone" placeholder="请输入手机号" maxlength="11"></el-input>
        </el-form-item>

        <el-form-item label="邮箱" prop="email">
          <el-input v-model="editForm.email" placeholder="请输入邮箱"></el-input>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitEditForm">保存修改</el-button>
      </template>
    </el-dialog>

    <!-- 更换头像对话框 -->
    <el-dialog
      title="更换头像"
      v-model="avatarDialogVisible"
      width="400px"
      :close-on-click-modal="false"
      :append-to-body="true"
    >
      <el-upload
        class="avatar-uploader"
        action=""
        :show-file-list="false"
        :on-change="handleFileChange"
        :before-upload="beforeAvatarUpload"
        accept="image/jpeg,image/png"
      >
        <img v-if="tempAvatarUrl" :src="tempAvatarUrl" class="avatar-preview">
        <el-button size="small" type="primary" v-else>选择图片</el-button>
        <div class="upload-hint">支持JPG、PNG格式，大小不超过2MB</div>
      </el-upload>

      <template #footer>
        <el-button @click="avatarDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmAvatarChange" :loading="uploading">确认更换</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { onMounted, ref, reactive, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import {
  ElMessage,
  ElForm,
  ElFormItem,
  ElInput,
  ElRadioGroup,
  ElRadio,
  ElDatePicker,
  ElButton,
  ElDialog,
  ElMessageBox,
  ElUpload,
  ElCascader,
  ElSelect,
  ElOption,
  ElCheckboxGroup,
  ElCheckbox,
  ElTag,
  ElInputNumber
} from 'element-plus'
import { UserFilled, Edit } from '@element-plus/icons-vue'
import websocket from '../api/websocket'
import socketService from '@/api/socket'
import axios from 'axios'
import profileApi from '@/api/userProfile'
import matchApi from '@/api/match'

const store = useStore()
const router = useRouter()
const error = ref('')
const user = ref(null)
const loading = ref(true)
const dialogVisible = ref(false)
const editFormRef = ref(null)
const avatarDialogVisible = ref(false)
const tempAvatarUrl = ref('')
const selectedFile = ref(null)
const uploading = ref(false)

/* 交友资料（原 UserProfile） */
const profileLoading = ref(false)
const userProfile = ref(null)
const isEditing = ref(false)
const profileFormRef = ref(null)
const submitLoading = ref(false)
const otherOccupation = ref('')
const hobbySports = ['跑步', '游泳', '篮球', '足球', '羽毛球', '乒乓球', '网球', '瑜伽', '健身', '骑行', '登山', '露营', '滑雪']
const hobbyArts = ['摄影', '绘画', '书法', '音乐', '吉他', '钢琴', '舞蹈', '话剧', '电影', '阅读', '写作', '插画']
const hobbyLife = ['烹饪', '烘焙', '咖啡', '茶艺', '养花', '宠物', '手工', '收纳', '旅行', '探店', '品酒', '茶道']
const hobbyFun = ['编程', '游戏', '动漫', '追星', '手办', '汉服', '露营', '钓鱼', '棋类', '星座', 'Vlog']
const cityOptions = ref([
  { name: '北京市', children: [{ name: '北京市' }] },
  { name: '上海市', children: [{ name: '上海市' }] },
  {
    name: '广东省',
    children: [
      { name: '广州市' }, { name: '深圳市' }, { name: '珠海市' }, { name: '佛山市' }, { name: '东莞市' }
    ]
  },
  {
    name: '江苏省',
    children: [
      { name: '南京市' }, { name: '苏州市' }, { name: '无锡市' }, { name: '常州市' }, { name: '徐州市' }
    ]
  },
  {
    name: '浙江省',
    children: [
      { name: '杭州市' }, { name: '宁波市' }, { name: '温州市' }, { name: '绍兴市' }, { name: '嘉兴市' }
    ]
  },
  {
    name: '四川省',
    children: [{ name: '成都市' }, { name: '绵阳市' }, { name: '德阳市' }, { name: '宜宾市' }]
  }
])
const commonOccupations = ref([
  '学生', '教师', '医生', '护士', '工程师', '程序员', '设计师',
  '销售', '市场', '运营', '客服', '人力资源', '财务', '律师',
  '公务员', '创业者', '自由职业者', '退休'
])
const profileForm = reactive({
  userId: '',
  nickname: '',
  signature: '',
  city: [],
  occupation: '',
  hobbies: '',
  selectedHobbies: [],
  height: null,
  weight: null,
  hometown: '',
  lifePhotos: []
})
const editForm = reactive({
  id: '',
  nickname: '',
  gender: 0,
  birthday: '',
  lunarBirthday: '',
  phone: '',
  email: ''
})
const formRules = {
  nickname: [
    { max: 20, message: '昵称长度不能超过20个字符', trigger: 'blur' }
  ],
  phone: [
    { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号格式', trigger: 'blur' }
  ],
  email: [
    { type: 'email', message: '请输入正确的邮箱格式', trigger: 'blur' }
  ]
}

/* 偏好设置相关 */
const currentUserId = ref(localStorage.getItem('userId') ? Number(localStorage.getItem('userId')) : null);
const preferenceLoading = ref(true);
const preferenceError = ref('');
const preferenceFormRef = ref(null);

const allTags = [
  { label: '旅行', value: '旅行' },
  { label: '电影', value: '电影' },
  { label: '音乐', value: '音乐' },
  { label: '美食', value: '美食' },
  { label: '健身', value: '健身' },
  { label: '阅读', value: '阅读' },
  { label: '游戏', value: '游戏' },
  { label: '摄影', value: '摄影' },
  { label: '宠物', value: '宠物' },
  { label: '烹饪', value: '烹饪' },
  { label: '运动', value: '运动' },
  { label: '艺术', value: '艺术' },
  { label: '科技', value: '科技' },
  { label: '理财', value: '理财' },
  { label: '户外', value: '户外' },
];

const preferenceForm = reactive({
  userId: currentUserId,
  minAge: 18,
  maxAge: 50,
  targetGender: 0,
  education: 0,
  maritalStatus: 0,
  minHeight: 150,
  maxHeight: 200,
  minWeight: 40,
  maxWeight: 80,
  maxDistance: 10000,
  preferredTags: []
})

const formatDate = (dateString, format = 'yyyy-MM-dd HH:mm:ss') => {
  if (!dateString) return ''
  const date = new Date(dateString)
  const pad = (num) => num.toString().padStart(2, '0')
  const year = date.getFullYear()
  const month = pad(date.getMonth() + 1)
  const day = pad(date.getDate())
  const hours = pad(date.getHours())
  const minutes = pad(date.getMinutes())
  const seconds = pad(date.getSeconds())
  return format
    .replace('yyyy', year)
    .replace('MM', month)
    .replace('dd', day)
    .replace('HH', hours)
    .replace('mm', minutes)
    .replace('ss', seconds)
}
const formatGender = (gender) => {
  switch(gender) {
    case 1: return '男'
    case 2: return '女'
    default: return '未知'
  }
}
const disableFutureDate = (time) => {
  return time.getTime() > Date.now()
}
const getPhotosList = (lifePhotos) => {
  if (!lifePhotos) return []
  try { return JSON.parse(lifePhotos).filter(Boolean) } catch { return [] }
}

const formatProfileDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleString('zh-CN', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit'
  })
}
const validateHobbyCount = (rule, value, callback) => {
  if (value && value.length > 10) {
    callback(new Error('最多选择10个爱好'))
  } else { callback() }
}
const handleHobbyChange = (newVal) => {
  const uniqueHobbies = [...new Set((newVal || []).filter((h) => h && String(h).trim() !== ''))]
  if (uniqueHobbies.length > 10) {
    uniqueHobbies.splice(10)
    ElMessage.warning('最多只能选择10个爱好')
  }
  if (uniqueHobbies.join(',') !== profileForm.selectedHobbies.join(',')) {
    nextTick(() => {
      profileForm.selectedHobbies = [...uniqueHobbies]
      profileForm.hobbies = uniqueHobbies.join(',')
    })
  }
}
const handleOccupationChange = (value) => {
  if (value !== 'other') { otherOccupation.value = '' }
}
const updateOtherOccupation = (value) => {
  profileForm.occupation = value
}
const fetchUserProfile = async () => {
  const userId = localStorage.getItem('userId')
  if (!userId) return
  try {
    profileLoading.value = true
    profileForm.userId = userId
    const res = await profileApi.getByUserId(userId)
    if (res.data.code == 200 && res.data.data) {
      userProfile.value = res.data.data
      if (userProfile.value.hobbies) {
        const arr = userProfile.value.hobbies.split(',').filter((h) => h.trim() !== '').slice(0, 10)
        profileForm.selectedHobbies = arr
        profileForm.hobbies = arr.join(',')
      }
    } else { userProfile.value = null }
  } catch (e) {
    console.error('查询交友资料失败:', e)
    userProfile.value = null
  } finally { profileLoading.value = false }
}
const handleAddProfile = () => {
  Object.keys(profileForm).forEach((key) => {
    if (key === 'userId') return
    if (key === 'selectedHobbies' || key === 'city') { profileForm[key] = []
    } else if (key === 'height' || key === 'weight') { profileForm[key] = null
    } else { profileForm[key] = '' }
  })
  profileForm.userId = localStorage.getItem('userId') || ''
  otherOccupation.value = ''
  isEditing.value = true
}
const handleEditProfile = () => {
  if (!userProfile.value) return
  Object.keys(profileForm).forEach((key) => {
    if (key === 'selectedHobbies' || key === 'city') return
    if (key === 'height' || key === 'weight') { profileForm[key] = userProfile.value[key] ?? null
    } else if (key === 'lifePhotos') {
      try { profileForm.lifePhotos = userProfile.value.lifePhotos ? JSON.parse(userProfile.value.lifePhotos) : [] } catch { profileForm.lifePhotos = [] }
    } else { profileForm[key] = userProfile.value[key] || '' }
  })
  if (userProfile.value.province && userProfile.value.city) {
    profileForm.city = [userProfile.value.province, userProfile.value.city]
  } else { profileForm.city = [] }
  if (userProfile.value.occupation && !commonOccupations.value.includes(userProfile.value.occupation)) {
    profileForm.occupation = 'other'
    otherOccupation.value = userProfile.value.occupation
  } else { profileForm.occupation = userProfile.value.occupation || ''; otherOccupation.value = '' }
  let hobbiesArray = []
  if (userProfile.value.hobbies) {
    hobbiesArray = userProfile.value.hobbies.split(',').filter((h) => h.trim() !== '').slice(0, 10)
  }
  nextTick(() => { profileForm.selectedHobbies = hobbiesArray })
  isEditing.value = true
}
const handleProfileCancel = () => {
  isEditing.value = false
  if (profileFormRef.value) {
    profileFormRef.value.resetFields()
    nextTick(() => { profileForm.selectedHobbies = []; profileForm.city = [] })
  }
  otherOccupation.value = ''
}
// 选择并上传生活照
const selectPhoto = () => {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = 'image/jpeg,image/png'
  input.onchange = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) { ElMessage.error('图片大小不能超过5MB'); return }
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('fileType', 2)
      const res = await axios.post('/message/api/v1/files/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      if (res.data.code == 200 && res.data.data?.accessUrl) {
        profileForm.lifePhotos.push(res.data.data.accessUrl)
        ElMessage.success('上传成功')
      } else {
        ElMessage.error(res.data.msg || '上传失败')
      }
    } catch (err) {
      console.error('上传生活照失败:', err)
      ElMessage.error('上传失败')
    }
  }
  input.click()
}
const removePhoto = (idx) => {
  profileForm.lifePhotos.splice(idx, 1)
}
const previewPhoto = (url) => {
  window.open(url, '_blank')
}

const handleProfileSubmit = async () => {
  const form = profileFormRef.value
  if (!form) { ElMessage.error('表单未就绪'); return }
  try {
    await form.validate()
    submitLoading.value = true
    let occupation = profileForm.occupation === 'other' ? (otherOccupation.value || '') : profileForm.occupation
    const submitData = {
      userId: profileForm.userId || localStorage.getItem('userId'),
      nickname: profileForm.nickname, signature: profileForm.signature,
      province: profileForm.city[0] || '', city: profileForm.city[1] || '',
      occupation, hobbies: profileForm.hobbies,
      height: profileForm.height, weight: profileForm.weight,
      hometown: profileForm.hometown,
      lifePhotos: JSON.stringify(profileForm.lifePhotos.filter(Boolean))
    }
    if (!submitData.userId) { ElMessage.error('用户ID不存在，请重新登录'); return }
    if (!submitData.province || !submitData.city) { ElMessage.error('请选择完整的省/市信息'); return }
    if (!submitData.occupation) { ElMessage.error('请选择或输入职业'); return }
    const res = await profileApi.saveOrUpdate(submitData)
    if (res.data.code == 200 && res.data.data) {
      ElMessage.success(userProfile.value ? '交友资料已更新' : '交友资料已保存')
      userProfile.value = res.data.data
      isEditing.value = false
    } else { ElMessage.error(res.data?.msg || '保存失败') }
  } catch (err) {
    if (err !== false) { ElMessage.error(err?.response?.data?.msg || '保存失败，请检查表单') }
  } finally { submitLoading.value = false }
}
const openEditForm = () => {
  editForm.id = user.value.id
  editForm.nickname = user.value.nickname || ''
  editForm.gender = user.value.gender !== undefined ? user.value.gender : 0
  editForm.birthday = user.value.birthday || ''
  editForm.lunarBirthday = user.value.lunarBirthday || ''
  editForm.phone = user.value.phone || ''
  editForm.email = user.value.email || ''
  dialogVisible.value = true
}
const submitEditForm = async () => {
  try {
    await editFormRef.value.validate()
    const formData = { ...editForm };
    if (formData.birthday) { formData.birthday = formData.birthday.replace(' ', 'T'); }
    if (formData.lunarBirthday) { formData.lunarBirthday = formData.lunarBirthday.replace(' ', 'T'); }
    const response = await axios.post('/user-service/api/v1/users/update', formData)
    if (response.data.success) {
      user.value = { ...user.value, ...editForm }
      ElMessage.success('信息更新成功')
      dialogVisible.value = false
    } else { ElMessage.error(response.data.message || '信息更新失败') }
  } catch (err) {
    if (err.name === 'ValidationError') return console.log('表单验证失败:', err)
    const errorMsg = err.response?.data?.message || '信息更新失败，请稍后重试'
    ElMessage.error(errorMsg)
    console.error('更新用户信息失败:', err)
  }
}
const handleAvatarClick = () => {
  ElMessageBox.confirm('是否要更换头像？', '确认更换', {
    confirmButtonText: '是', cancelButtonText: '否', type: 'warning'
  }).then(() => {
    avatarDialogVisible.value = true; tempAvatarUrl.value = ''; selectedFile.value = null;
  }).catch(() => {});
};
const handleAvatarError = (e) => { e.target.src = ''; };
const handleFileChange = (file) => {
  selectedFile.value = file.raw;
  const reader = new FileReader();
  reader.onload = (e) => { tempAvatarUrl.value = e.target.result; };
  reader.readAsDataURL(file.raw);
};
const beforeAvatarUpload = (file) => {
  const isJPG = file.type === 'image/jpeg' || file.type === 'image/png';
  const isLt2M = file.size / 1024 / 1024 < 2;
  if (!isJPG) { ElMessage.error('只能上传JPG/PNG格式的图片!'); }
  if (!isLt2M) { ElMessage.error('图片大小不能超过2MB!'); }
  return isJPG && isLt2M;
};
const confirmAvatarChange = async () => {
  if (!selectedFile.value) { ElMessage.warning('请选择图片'); return; }
  try {
    uploading.value = true;
    const formData = new FormData();
    formData.append('file', selectedFile.value);
    formData.append('userId', user.value.id);
    formData.append('fileType', 2);
    const response = await axios.post('/message-service/api/v1/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    if (response.data.success && response.data.data?.accessUrl) {
      const newAvatarUrl = response.data.data.accessUrl;
      await axios.post('/user-service/api/v1/users/update', { id: user.value.id, avatar: newAvatarUrl });
      user.value.avatar = newAvatarUrl;
      ElMessage.success('头像更新成功');
      avatarDialogVisible.value = false;
    } else { ElMessage.error('头像上传失败'); }
  } catch (err) {
    console.error('更换头像失败:', err);
    ElMessage.error('更换头像失败，请稍后重试');
  } finally { uploading.value = false; }
};
const fetchUserInfo = async () => {
  try {
    loading.value = true
    const userData = await store.dispatch('fetchUserInfo')
    user.value = userData
    await fetchUserProfile()
  } catch (err) { error.value = store.state.error || '获取用户信息失败' }
  finally { loading.value = false }
}

const getPreference = async () => {
  if (!currentUserId.value) { ElMessage.error('请先登录'); router.push('/login'); preferenceLoading.value = false; return; }
  try {
    const res = await matchApi.getPreference(currentUserId.value);
    if (res.data.code == 200) {
      const data = res.data.data;
      if (typeof data.preferredTags === 'string') { data.preferredTags = data.preferredTags.split(',').filter(tag => tag); }
      Object.assign(preferenceForm, data);
    } else { preferenceError.value = res.data.msg || '获取偏好失败'; }
  } catch (err) { preferenceError.value = err.response?.data?.msg || '获取偏好失败'; }
  finally { preferenceLoading.value = false; }
};

const savePreference = async () => {
  const formData = { ...preferenceForm, preferredTags: preferenceForm.preferredTags.join(',') };
  try {
    const res = await matchApi.savePreference(formData);
    if (res.data.success || res.data.code === 200) { ElMessage.success('保存成功');
    } else { ElMessage.error(res.data.msg || '保存失败'); }
  } catch (err) { ElMessage.error('保存失败，请重试'); console.error('保存偏好设置错误:', err); }
};

const resetPreferenceForm = () => { getPreference(); ElMessage.info('已重置为当前设置'); };

const handleLogout = async () => {
  try { await store.dispatch('logout'); websocket.close(); socketService.close(); router.push('/login');
  } catch (err) {
    console.error('Logout error:', err);
    localStorage.removeItem('accessToken'); localStorage.removeItem('userId'); localStorage.removeItem('username');
    store.commit('setUser', null); router.push('/login');
  }
};

onMounted(() => { fetchUserInfo(); getPreference(); });
</script>

<style scoped>
.home-page {
  min-height: 100%;
  box-sizing: border-box;
}

.home-main {
  margin: 0;
  padding: 0;
}

main {
  max-width: 100%;
  margin: 0 auto;
  padding: 0;
  box-sizing: border-box;
}


/* Toolbar */
.app-workbench__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 20px;
  padding-bottom: 16px;
  margin-bottom: 0;
  border-bottom: 1px solid var(--app-border-light);
}

.toolbar-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.toolbar-subtitle {
  font-size: 0.8125rem;
  color: var(--app-text-muted);
  line-height: 1.4;
}

.logout-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background-color: #fff;
  color: var(--app-color-primary);
  border: 1.5px solid rgba(255, 126, 126, 0.3);
  border-radius: var(--app-radius-md);
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.25s ease;
  line-height: 1.4;
}

.logout-btn:hover {
  background-color: var(--app-color-primary-soft);
  border-color: var(--app-color-primary);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(255, 126, 126, 0.15);
}

.logout-btn:active {
  transform: translateY(0);
}

/* Loading & Error */
.loading {
  text-align: center;
  padding: 60px 0;
  color: var(--app-text-secondary);
}

.spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto 15px;
  border: 4px solid rgba(255, 126, 126, 0.15);
  border-radius: 50%;
  border-top-color: var(--app-color-primary);
  animation: spin 0.8s ease-in-out infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-message {
  color: #e74c3c;
  padding: 14px 18px;
  background: #fef0f0;
  border-radius: var(--app-radius-md);
  margin-bottom: 20px;
  border: 1px solid #fde2e2;
  font-size: 0.875rem;
  line-height: 1.5;
}

/* User Header */
.user-header {
  position: relative;
  border-radius: var(--app-radius-lg);
  overflow: hidden;
}

.user-header-content {
  position: relative;
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 28px 32px;
  border-bottom: 1px solid var(--app-border-subtle);
}

.user-avatar-wrapper {
  flex-shrink: 0;
}

.avatar {
  position: relative;
  width: 84px;
  height: 84px;
  border-radius: 50%;
  background: var(--app-bg-elevated);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 3px solid #fff;
  box-shadow: 0 3px 12px rgba(255, 126, 126, 0.2);
}

.avatar:hover {
  transform: scale(1.06);
  box-shadow: 0 6px 20px rgba(255, 126, 126, 0.3);
}

.avatar-img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.avatar-badge {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 26px;
  height: 26px;
  background: var(--app-color-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2.5px solid #fff;
  box-shadow: 0 2px 6px rgba(255, 126, 126, 0.3);
  transition: transform 0.25s ease;
}

.avatar:hover .avatar-badge {
  transform: rotate(-15deg);
}

.user-welcome {
  flex: 1;
  min-width: 0;
}

.user-greeting {
  display: flex;
  align-items: center;
  font-size: 1.375rem;
  color: var(--app-text-primary);
  margin: 0 0 6px;
  font-weight: 600;
}

.user-greeting svg {
  color: var(--app-color-primary);
}

.user-member-info {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 0.8125rem;
  color: var(--app-text-muted);
}

.user-member-info svg {
  flex-shrink: 0;
  color: var(--app-color-primary-light);
}

/* Info Section */
.info-section {
  padding: 28px 32px;
  border-bottom: 1px solid var(--app-border-subtle);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.section-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.section-accent {
  display: block;
  width: 4px;
  height: 20px;
  background: var(--app-gradient-primary);
  border-radius: var(--app-radius-full);
  flex-shrink: 0;
}

.info-section h3 {
  font: var(--app-font-heading);
  color: var(--app-text-primary);
  margin: 0;
}

.edit-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 18px;
  background: var(--app-bg-soft);
  border-radius: var(--app-radius-md);
  border: 1px solid var(--app-border-subtle);
  transition: all 0.25s ease;
}

.info-item:hover {
  border-color: rgba(255, 126, 126, 0.2);
  background: rgba(255, 126, 126, 0.04);
  transform: translateY(-1px);
  box-shadow: 0 3px 10px rgba(0,0,0,0.05);
}

.info-item-icon {
  font-size: 1.25rem;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--app-bg-elevated);
  border-radius: var(--app-radius-sm);
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}

.info-item-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.label {
  color: var(--app-text-secondary);
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.value {
  color: var(--app-text-primary);
  font-size: 0.9375rem;
  font-weight: 500;
  word-break: break-word;
}

/* Dating Card */
.dating-card {
  border-bottom: 1px solid var(--app-border-subtle);
  overflow: hidden;
}

.dating-banner {
  padding: 1.75rem 2rem;
  background: var(--app-gradient-accent);
  color: #fff;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.dating-banner::before {
  content: '';
  position: absolute;
  top: -30%;
  right: -10%;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  pointer-events: none;
}

.dating-banner-icon {
  margin-bottom: 10px;
  opacity: 0.9;
}

.dating-banner-icon svg {
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
}

.dating-banner-title {
  font-size: 1.35rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  position: relative;
  z-index: 1;
}

.dating-banner-desc {
  margin: 0 auto;
  font-size: 0.875rem;
  opacity: 0.92;
  line-height: 1.6;
  max-width: 36rem;
  position: relative;
  z-index: 1;
}

.dating-body {
  padding: 1.75rem 2rem 2rem;
}

.dating-loading {
  text-align: center;
  padding: 2rem;
  color: var(--app-text-secondary);
}

.dating-empty {
  text-align: center;
  padding: 2.5rem 1rem;
}

.dating-empty-icon {
  color: var(--app-color-primary);
  margin-bottom: 0.75rem;
}

.dating-empty-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--app-text-primary);
  margin: 0 0 0.5rem;
}

.dating-empty-desc {
  font-size: 0.875rem;
  color: var(--app-text-secondary);
  margin: 0 0 1.25rem;
  max-width: 22rem;
  margin-left: auto;
  margin-right: auto;
}

.dating-section-head,
.dating-form-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--app-border-subtle);
}

.dating-section-head h2,
.dating-form-head h2 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--app-text-primary);
  position: relative;
  padding-left: 14px;
}

.dating-section-head h2::before,
.dating-form-head h2::before {
  content: '';
  position: absolute;
  left: 0;
  top: 2px;
  bottom: 2px;
  width: 3px;
  background: var(--app-color-primary);
  border-radius: var(--app-radius-full);
}

.dating-edit-link {
  font-size: 0.875rem;
}

.dating-fields-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}
@media (max-width: 768px) {
  .dating-fields-grid { grid-template-columns: 1fr; }
}

.dating-field-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.1rem 1.2rem;
  background: var(--app-bg-elevated);
  border: 1px solid var(--app-border-subtle);
  border-radius: var(--app-radius-md);
  box-shadow: var(--app-shadow-sm);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.dating-field-item:hover {
  transform: translateY(-2px);
  box-shadow: var(--app-shadow-card-hover);
  border-color: rgba(139, 124, 247, 0.2);
}

.dating-field-item--wide {
  grid-column: 1 / -1;
}

.dating-field-icon {
  font-size: 1.4rem;
  min-width: 2.5rem;
  text-align: center;
  line-height: 1.2;
}

.dating-field-info {
  flex: 1;
  min-width: 0;
}

.dating-field-info h3 {
  margin: 0 0 0.3rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--app-text-secondary);
  letter-spacing: 0.02em;
}

.dating-field-info p {
  margin: 0;
  font-size: 0.9375rem;
  color: var(--app-text-primary);
  line-height: 1.5;
  word-break: break-word;
}

.dating-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.dating-dash { color: var(--app-text-muted); }

.photo-upload-area { max-width: 400px; }
.photo-grid { display: flex; flex-wrap: wrap; gap: 8px; }
.photo-grid .photo-item {
  width: 80px; height: 80px; border-radius: 8px; overflow: hidden;
  position: relative; border: 1px solid var(--app-border-light);
}
.photo-grid .photo-item img { width: 100%; height: 100%; object-fit: cover; }
.photo-grid .photo-del {
  position: absolute; top: -4px; right: -4px; width: 20px; height: 20px;
  border-radius: 50%; background: #e74c3c; color: white; border: none;
  font-size: 14px; cursor: pointer; display: flex; align-items: center; justify-content: center;
}
.photo-grid .photo-add {
  width: 80px; height: 80px; border-radius: 8px; border: 2px dashed var(--app-border-light);
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  cursor: pointer; color: var(--app-text-muted); font-size: 0.75rem; gap: 4px;
}
.photo-grid .photo-add:hover { border-color: var(--app-color-primary); color: var(--app-color-primary); }
.photo-tip { font-size: 0.75rem; color: var(--app-text-muted); margin-top: 6px; }
.photos-preview { display: flex; gap: 6px; flex-wrap: wrap; }
.photos-preview img { width: 60px; height: 60px; border-radius: 6px; object-fit: cover; cursor: pointer; }

.dating-profile-form {
  max-width: 920px;
}

.dating-profile-form ::v-deep .el-form-item__label {
  font-weight: 500;
  color: var(--app-text-secondary);
}

.dating-other-job { margin-top: 10px; max-width: 400px; }

.hobby-select-container {
  background: var(--app-bg-soft);
  padding: 16px;
  border-radius: var(--app-radius-md);
  border: 1px solid var(--app-border-subtle);
  width: 100%;
}

.hobby-category { margin-bottom: 14px; }
.hobby-category:last-child { margin-bottom: 0; }

.category-label {
  display: block;
  font-weight: 600;
  font-size: 0.8125rem;
  color: var(--app-color-primary-dark);
  margin-bottom: 8px;
  padding-left: 4px;
}

.hobby-checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 14px;
}

.hobby-tip {
  font-size: 0.75rem;
  color: var(--app-text-secondary);
  margin-top: 8px;
}

.dating-form-actions {
  margin-top: 8px;
  display: flex;
  gap: 12px;
}

/* Preference Section */
.preference-section {
  padding: 28px 32px 32px;
}

.preference-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--app-border-subtle);
}

.preference-header-left {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.section-accent--pref { margin-top: 4px; }

.preference-header h2 {
  color: var(--app-text-primary);
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0 0 4px;
}

.preference-header p {
  color: var(--app-text-secondary);
  font-size: 0.8125rem;
  margin: 0;
  line-height: 1.5;
}

.preference-form {
  background-color: transparent;
  padding: 0;
  border-radius: 0;
}

.preference-form .form-control {
  width: 100%;
  max-width: 280px;
}

.preference-form .range-container {
  display: flex;
  align-items: center;
  max-width: 280px;
  width: 100%;
}

.preference-form .range-input {
  flex: 1;
  max-width: none;
}

.preference-form .range-container .range-input:first-child {
  margin-right: 10px;
}

.preference-form .range-container .range-input:last-child {
  margin-left: 10px;
}

.preference-form .range-separator {
  color: var(--app-text-muted);
  padding: 0 5px;
  font-weight: 500;
}

.preference-form .tags-container { max-width: 600px; }

.preference-form .tags-checkbox-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 10px;
}

.preference-form .tag-checkbox { margin: 0 !important; }

.preference-form .tags-hint {
  font-size: 0.75rem;
  color: var(--app-text-muted);
  margin-top: 5px;
}

.preference-form .form-actions {
  margin-top: 24px;
  display: flex;
  gap: 15px;
  padding-top: 20px;
  border-top: 1px solid var(--app-border-light);
}

.preference-form .save-btn {
  padding: 10px 28px;
  font-weight: 500;
  border-radius: var(--app-radius-md);
}

.preference-form .reset-btn {
  padding: 10px 28px;
  border-radius: var(--app-radius-md);
}

.preference-loading {
  text-align: center;
  padding: 40px 0;
}

.preference-section .spinner {
  width: 36px;
  height: 36px;
  margin: 0 auto 12px;
  border: 3.5px solid rgba(255, 126, 126, 0.12);
  border-top-color: var(--app-color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.preference-section .error-message {
  color: #f56c6c;
  background: #fef0f0;
  padding: 12px 15px;
  border-radius: var(--app-radius-sm);
  margin-bottom: 20px;
  border: 1px solid #fde2e2;
}

.preference-section ::v-deep .el-form-item { margin-bottom: 22px; }
.preference-section ::v-deep .el-form-item__label {
  font-weight: 500;
  color: var(--app-text-secondary);
  font-size: 0.875rem;
}
.preference-section ::v-deep .el-checkbox { margin-bottom: 6px; }
.preference-section ::v-deep .el-checkbox__label {
  color: var(--app-text-secondary);
  font-size: 0.875rem;
}
.preference-section ::v-deep .el-checkbox__input.is-checked .el-checkbox__inner {
  background-color: var(--app-color-primary);
  border-color: var(--app-color-primary);
}
.preference-section ::v-deep .el-checkbox__input.is-checked + .el-checkbox__label {
  color: var(--app-color-primary-dark);
}

/* Dialogs */
.edit-form { margin-top: 20px; }
.edit-form ::v-deep .el-form-item__label {
  font-weight: 500;
  color: var(--app-text-secondary);
}

.form-hint {
  color: var(--app-text-muted);
  font-size: 0.75rem;
  margin-top: 5px;
  line-height: 1.5;
}

.avatar-uploader { text-align: center; padding: 20px 0; }

.avatar-preview {
  width: 180px;
  height: 180px;
  border-radius: 50%;
  object-fit: cover;
  margin: 0 auto 15px;
  display: block;
  border: 3px solid var(--app-border-light);
  box-shadow: var(--app-shadow-card);
}

.upload-hint {
  color: var(--app-text-secondary);
  font-size: 0.75rem;
  margin-top: 10px;
  line-height: 1.5;
}

/* Responsive */
@media (max-width: 768px) {
  .user-header-content {
    flex-direction: column;
    align-items: center;
    text-align: center;
    padding: 20px 16px;
  }

  .user-greeting {
    font-size: 1.125rem;
    justify-content: center;
  }

  .user-member-info { justify-content: center; }

  .info-section { padding: 20px 16px; }

  .info-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .info-item { padding: 12px 14px; }
  .dating-banner { padding: 1.25rem 16px; }
  .dating-body { padding: 1.25rem 16px 1.5rem; }
  .dating-fields-grid { grid-template-columns: 1fr; }
  .dating-form-actions { flex-direction: column; align-items: stretch; }
  .dating-form-actions .el-button { width: 100%; }

  .preference-section { padding: 20px 16px; }
  .preference-form .form-control,
  .preference-form .range-container {
    max-width: 100%;
  }
  .preference-form .form-actions { flex-direction: column; align-items: stretch; }
  .preference-form .save-btn,
  .preference-form .reset-btn { width: 100%; }
  .section-header { flex-direction: column; align-items: flex-start; gap: 12px; }
}
</style>
